import React from 'react';
import PrintListado, {
  getPrintStyles,
  escapeHtml,
  buildHeaderHtml,
} from 'utils/PrintListado';

/**
 * Estilos adicionales para la impresión de conciliación bancaria.
 */
const conciliacionPrintStyles = `
  .conc-summary { margin: 12px 0; padding: 10px; border: 1px solid #ccc; border-radius: 4px; }
  .conc-summary-row { display: flex; justify-content: space-between; margin: 3px 0; font-size: 12px; }
  .conc-summary-label { font-weight: bold; }
  .conc-check { text-align: center; font-weight: bold; }
  .conc-check-si { color: #047857; }
  .conc-check-no { color: #9F1239; }
  .conc-status { display: inline-block; padding: 2px 8px; border-radius: 10px; font-size: 11px; font-weight: bold; }
  .status-pendiente { background: #FEF3C7; color: #92400E; }
  .status-enproceso { background: #E0F2FE; color: #075985; }
  .status-condiferencias { background: #FFE4E6; color: #9F1239; }
  .status-conciliada { background: #DCFCE7; color: #047857; }
  .status-anulada { background: #F1F5F9; color: #64748B; }
`;

const fmtMoney = (v) => {
  const n = Number(v || 0);
  return n.toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const statusClass = (s) => {
  const m = {
    Pendiente: 'status-pendiente',
    'En proceso': 'status-enproceso',
    'Con diferencias': 'status-condiferencias',
    Conciliada: 'status-conciliada',
    Anulada: 'status-anulada',
  };
  return m[s] || 'status-pendiente';
};

/**
 * Componente reutilizable de impresión de conciliación bancaria.
 *
 * @param {object}  conciliacion - Encabezado de la conciliación.
 * @param {Array}   detalles     - Partidas de la conciliación.
 * @param {object}  [empresa]    - Datos de empresa para encabezado.
 * @param {string}  [label]      - Texto del botón de impresión.
 * @param {object}  [buttonProps] - Props del botón.
 */
const PrintConciliacion = ({
  conciliacion = {},
  detalles = [],
  empresa,
  label = 'Imprimir conciliación',
  buttonProps = {},
}) => {
  const enc = conciliacion;
  const statusCls = statusClass(enc.status);

  const buildBody = () => {
    const header = buildHeaderHtml(empresa, '', 'Conciliación Bancaria', '');

    const summaryHtml = `
      <div class="conc-summary">
        <div class="conc-summary-row">
          <span class="conc-summary-label">Nº Conciliación:</span>
          <span>${escapeHtml(String(enc.numero || ''))}</span>
        </div>
        <div class="conc-summary-row">
          <span class="conc-summary-label">Cuenta banco:</span>
          <span>${escapeHtml(enc.cuenta_banco || '')}</span>
        </div>
        <div class="conc-summary-row">
          <span class="conc-summary-label">Cuenta contable:</span>
          <span>${escapeHtml(enc.cuenta_contable || '—')}</span>
        </div>
        <div class="conc-summary-row">
          <span class="conc-summary-label">Período:</span>
          <span>${escapeHtml(enc.fecha_inicio || '')} al ${escapeHtml(enc.fecha_fin || '')}</span>
        </div>
        <div class="conc-summary-row">
          <span class="conc-summary-label">Status:</span>
          <span class="conc-status ${statusCls}">${escapeHtml(enc.status || 'Pendiente')}</span>
        </div>
        <div class="conc-summary-row">
          <span class="conc-summary-label">Saldo anterior (libros):</span>
          <span>${fmtMoney(enc.balance_inicial)}</span>
        </div>
        <div class="conc-summary-row">
          <span class="conc-summary-label">Balance Libro:</span>
          <span>${fmtMoney(enc.balance_libro)}</span>
        </div>
        <div class="conc-summary-row">
          <span class="conc-summary-label">Balance Banco:</span>
          <span>${fmtMoney(enc.balance_banco)}</span>
        </div>
        <div class="conc-summary-row">
          <span class="conc-summary-label">Balance Conciliado:</span>
          <span>${fmtMoney(enc.balance_conciliado)}</span>
        </div>
        <div class="conc-summary-row">
          <span class="conc-summary-label">Diferencia:</span>
          <span>${fmtMoney(enc.diferencia)}</span>
        </div>
        ${enc.observaciones ? `<div class="conc-summary-row"><span class="conc-summary-label">Observaciones:</span><span>${escapeHtml(enc.observaciones)}</span></div>` : ''}
      </div>
    `;

    const thead = `
      <tr>
        <th>Fecha</th>
        <th>Documento</th>
        <th>Número</th>
        <th>Concepto</th>
        <th class="align-right">Débito</th>
        <th class="align-right">Crédito</th>
        <th class="align-center">Conciliado</th>
      </tr>
    `;

    const rows = detalles
      .map(
        (d) => `
        <tr>
          <td>${escapeHtml(d.fecha || '')}</td>
          <td>${escapeHtml(d.documento || '')}</td>
          <td>${escapeHtml(String(d.numero || ''))}</td>
          <td>${escapeHtml(d.concepto || d.datafiscal || '')}</td>
          <td class="align-right">${fmtMoney(d.debito)}</td>
          <td class="align-right">${fmtMoney(d.credito)}</td>
          <td class="conc-check ${d.conciliado === 'S' ? 'conc-check-si' : 'conc-check-no'}">${d.conciliado === 'S' ? '✔' : '—'}</td>
        </tr>
      `
      )
      .join('');

    const totDeb = detalles.reduce((s, d) => s + Number(d.debito || 0), 0);
    const totCred = detalles.reduce((s, d) => s + Number(d.credito || 0), 0);

    const totalRow = `
      <tr>
        <td colspan="4" style="font-weight:bold; text-align:right;">TOTALES</td>
        <td class="align-right" style="font-weight:bold;">${fmtMoney(totDeb)}</td>
        <td class="align-right" style="font-weight:bold;">${fmtMoney(totCred)}</td>
        <td></td>
      </tr>
    `;

    const tableHtml = `<table class="print-table"><thead>${thead}</thead><tbody>${rows}${totalRow}</tbody></table>`;

    return header + summaryHtml + tableHtml;
  };

  return (
    <PrintListado
      bodyHtml={buildBody()}
      extraStyles={`${getPrintStyles()}${conciliacionPrintStyles}`}
      skipHeader
      printButtonLabel={label}
      printButtonProps={buttonProps}
    />
  );
};

export default PrintConciliacion;
